/**
 * Precise triangle collision for a model used as a collider (`collisionMeshesData` entries with
 * meshType 'custom': an uploaded GLB / glTF, or a scene model reused as collision).
 *
 * The triangles are read once from the collider entity's render meshes in the ENTITY's local
 * space and baked into world space with the entity's current world matrix, re-baked whenever that
 * matrix changes (MeshCollision does the re-bake lazily, on the next query). So walk, fly, the
 * drone and picking all collide with the mesh where the entity is now: after loading, and while
 * an editor gizmo moves it.
 */
import * as pc from 'playcanvas';
import { MeshCollision } from './MeshCollision';

/** Cap against huge uploads (the triangles are held twice: entity-local and baked). */
const MAX_TRIANGLES = 2_000_000;

interface TriangleColliderNode extends pc.GraphNode {
    _triangleCollider?: MeshCollision;
    /** The world matrix the collider was last baked with; compared on every lookup. */
    _triangleColliderMatrix?: Float32Array;
}

/**
 * The file name the engine picks its parser from. A blob: URL (an unsaved upload in the desktop
 * app) carries none, so the collider's own file name (`meshData` / `name`) stands in for it.
 */
export function colliderFileName(url: string, name?: unknown): string {
    if (url.startsWith('blob:')) return typeof name === 'string' ? name : '';
    return url.split('?')[0].split('#')[0];
}

/** Only GLB / glTF load as collision: the engine has no OBJ, FBX or STL parser. */
export function isLoadableColliderFile(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return ext === 'glb' || ext === 'gltf';
}

/**
 * Put a loaded model's render hierarchy under `entity` (its children re-parented, the wrapper
 * dropped), so the entity's own transform — the collider's placement — places the model.
 */
export function addContainerMeshes(entity: pc.Entity, resource: pc.ContainerResource): void {
    const renderEntity = resource.instantiateRenderEntity();
    while (renderEntity.children.length > 0) {
        entity.addChild(renderEntity.children[0]);
    }
    renderEntity.destroy();
}

/** Triangles (9 floats each) of every render mesh under `entity`, in `entity`'s local space. */
function extractLocalTriangles(entity: pc.Entity): {
    tris: Float32Array;
    bounds: { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number };
} | null {
    const out: number[] = [];
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    const pos: number[] = [];
    const idx: number[] = [];
    const v = new pc.Vec3();
    const visit = (node: pc.GraphNode, toEntity: pc.Mat4) => {
        const render = (node as pc.Entity).render;
        if (render && render.meshInstances) {
            for (const mi of render.meshInstances) {
                const mesh = mi.mesh;
                if (!mesh) continue;
                pos.length = 0;
                const vcount = mesh.getPositions(pos);
                if (!vcount) continue;
                const local = new Float32Array(vcount * 3);
                for (let i = 0; i < vcount; i++) {
                    v.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);
                    toEntity.transformPoint(v, v);
                    local[i * 3] = v.x; local[i * 3 + 1] = v.y; local[i * 3 + 2] = v.z;
                }
                const pushVertex = (vi: number) => {
                    const x = local[vi * 3], y = local[vi * 3 + 1], z = local[vi * 3 + 2];
                    out.push(x, y, z);
                    if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
                    if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
                };
                idx.length = 0;
                const icount = mesh.getIndices(idx);
                if (icount && idx.length >= 3) {
                    for (let i = 0; i + 2 < idx.length && out.length < MAX_TRIANGLES * 9; i += 3) {
                        pushVertex(idx[i]); pushVertex(idx[i + 1]); pushVertex(idx[i + 2]);
                    }
                } else {
                    for (let i = 0; i + 2 < vcount && out.length < MAX_TRIANGLES * 9; i += 3) {
                        pushVertex(i); pushVertex(i + 1); pushVertex(i + 2);
                    }
                }
            }
        }
        for (const child of node.children) {
            visit(child, new pc.Mat4().mul2(toEntity, child.getLocalTransform()));
        }
    };
    visit(entity, new pc.Mat4());
    if (out.length < 9) return null;
    return { tris: new Float32Array(out), bounds: { minX, minY, minZ, maxX, maxY, maxZ } };
}

/** Give `node` a triangle collider holding triangles in its local space (baked on lookup). */
export function setTriangleCollider(node: pc.GraphNode, collider: MeshCollision): void {
    const n = node as TriangleColliderNode;
    n._triangleCollider = collider;
    n._triangleColliderMatrix = undefined;
}

/**
 * Build the entity's triangle collider from its render meshes. Returns null (no collider) when
 * the model has no triangles.
 */
export function attachTriangleCollider(entity: pc.Entity): MeshCollision | null {
    const local = extractLocalTriangles(entity);
    if (!local) return null;
    const collider = new MeshCollision(local.tris, local.bounds);
    setTriangleCollider(entity, collider);
    return collider;
}

/** The node's triangle collider, baked to the node's current world transform; null without one. */
export function triangleColliderOf(node: pc.GraphNode): MeshCollision | null {
    const n = node as TriangleColliderNode;
    const collider = n._triangleCollider;
    if (!collider) return null;
    const world = node.getWorldTransform().data;
    let last = n._triangleColliderMatrix;
    let changed = !last;
    if (last) {
        for (let i = 0; i < 16; i++) {
            if (last[i] !== world[i]) { changed = true; break; }
        }
    }
    if (changed) {
        if (!last) last = n._triangleColliderMatrix = new Float32Array(16);
        last.set(world);
        // MeshCollision's "bake the raw triangles with this matrix" setter: it snapshots the
        // matrix and re-bakes on the next query, so a gizmo drag costs nothing until then.
        collider.setSplatTransform({ data: world });
    }
    return collider;
}
